// src/pages/VerifyPhone.tsx
// Step 5 of 5 - WhatsApp verification
// IMPORTANT: Navigates to /payment after verification (NOT /dashboard)
// Allows user to edit phone number if entered incorrectly
// Styled to match app dashboard

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getSupabase, currentUser } from "@/lib/supabase";
import { ENV } from "@/lib/env";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all duration-500 ${
            i < current 
              ? "w-8 bg-[#a78bfa]" 
              : i === current 
                ? "w-8 bg-[#a78bfa]/40" 
                : "w-3 bg-[#f1f5f9]"
          }`}
        />
      ))}
    </div>
  );
}

export default function VerifyPhone() {
  const [phone, setPhone] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const supabase = getSupabase();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Load phone from database
  useEffect(() => {
    (async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
          navigate("/signup");
          return;
        }
        setUserId(user.id);
        
        const { data, error } = await supabase
          .from("clients")
          .select("phone")
          .eq("user_id", user.id)
          .single();
        if (error || !data?.phone) {
          setErr("Phone not found. Please go back and enter your details.");
          return;
        }
        setPhone(data.phone);
        setNewPhone(data.phone);
      } catch (e) {
        setErr("Could not load your phone number.");
      }
    })();
  }, [supabase, navigate]);

  // Save updated phone number
  const saveNewPhone = async () => {
    if (!newPhone || newPhone.length < 13 || !newPhone.startsWith("+44")) {
      setErr("Please enter a valid UK phone number");
      return;
    }
    
    setLoading(true);
    setErr(null);
    
    try {
      const { error } = await supabase
        .from("clients")
        .update({ phone: newPhone })
        .eq("user_id", userId);
      
      if (error) throw error;
      
      setPhone(newPhone);
      setEditingPhone(false);
      setOtpSent(false);
      setOtp("");
    } catch (e: any) {
      setErr(e.message || "Failed to update phone number");
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    setErr(null);
    if (!phone) return setErr("Phone number is missing.");
    if (!consent) return setErr("Please agree to receive WhatsApp messages.");

    setLoading(true);
    try {
      const res = await fetch(`${ENV.EDGE_BASE}/send-whatsapp-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ phone }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to send code");
      }
      setOtpSent(true);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (e: any) {
      setErr(e.message || "Failed to send WhatsApp message");
    } finally {
      setLoading(false);
    }
  };

  const referralCode = localStorage.getItem("referral_code");

  const verifyOtp = async () => {
    setErr(null);
    if (otp.length !== 6) return setErr("Enter the 6-digit code");
    
    setLoading(true);
    try {
      const res = await fetch(`${ENV.EDGE_BASE}/verify-whatsapp-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          phone,
          otp,
          referral_code: referralCode,
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Invalid code");
      }
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user");
      
      await supabase
        .from("clients")
        .update({
          phone_verified: true,
          whatsapp_consent: true,
          whatsapp_consented_at: new Date().toISOString(),
        })
        .eq("user_id", user.id);
      
      localStorage.removeItem("referral_code");
      
      // ✅ Navigate to payment after verification
      navigate("/payment");
    } catch (e: any) {
      setErr(e.message || "Verification failed");
      setOtp("");
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleOtpInput = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    if (digit) {
      const newOtp = otp.slice(0, index) + digit + otp.slice(index + 1);
      setOtp(newOtp.slice(0, 6));
      if (index < 5) inputRefs.current[index + 1]?.focus();
      if (newOtp.length === 6) setTimeout(() => verifyOtp(), 150);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (otp[index]) {
        setOtp(otp.slice(0, index) + otp.slice(index + 1));
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        setOtp(otp.slice(0, index - 1) + otp.slice(index));
      }
    }
  };

  // Format phone for display
  const formatPhone = (p: string) => {
    if (!p) return "";
    return p.replace(/(\+44)(\d{4})(\d{3})(\d{3})/, "$1 $2 $3 $4");
  };

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-gray-500 hover:text-[#0f172a] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">Back</span>
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#a78bfa] flex items-center justify-center">
            <svg className="w-5 h-5 text-[#0f172a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="font-semibold text-[#0f172a]">Input</span>
        </div>
        <div className="w-16"></div>
      </header>

      {/* Main Content */}
      <main className="flex flex-col items-center px-4 py-8">
        <div className="w-full max-w-md">
          
          {/* Title */}
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#25D366]/10 border border-[#25D366]/30 flex items-center justify-center">
              <svg className="w-7 h-7 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">
              {editingPhone ? "Update number" : otpSent ? "Enter the code" : "Verify WhatsApp"}
            </h1>
            <p className="text-gray-500 text-sm">
              {editingPhone 
                ? "Enter your correct phone number"
                : otpSent 
                  ? <>We sent a code to <span className="text-[#0f172a]">{formatPhone(phone)}</span></>
                  : "We'll send you a verification code"
              }
            </p>
          </div>

          {/* Main Card */}
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-5">

            {/* EDITING PHONE NUMBER */}
            {editingPhone ? (
              <>
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    Phone Number (UK)
                  </label>
                  <PhoneInput
                    country="gb"
                    onlyCountries={["gb"]}
                    preferredCountries={["gb"]}
                    value={newPhone.replace("+", "")}
                    onChange={(phone) => {
                      let cleaned = phone || "";
                      if (cleaned.startsWith("440")) cleaned = cleaned.substring(2);
                      cleaned = cleaned.replace(/^0+/, "");
                      if (!cleaned.startsWith("44")) cleaned = "44" + cleaned;
                      setNewPhone("+" + cleaned);
                    }}
                    placeholder="7123 456 789"
                    inputProps={{ required: true, autoFocus: true }}
                    containerClass="w-full"
                    inputClass="!w-full !h-12 !bg-[#f1f5f9] !border !border-[#e2e8f0] !rounded-xl !text-[#0f172a] !text-base !pl-14 focus:!outline-none focus:!border-[#a78bfa]/50 !transition-all"
                    buttonClass="!bg-[#f1f5f9] !border !border-[#e2e8f0] !rounded-l-xl !h-12 hover:!bg-[#f1f5f9]"
                    dropdownClass="!bg-[#f1f5f9] !border-[#e2e8f0] !rounded-xl"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setEditingPhone(false);
                      setNewPhone(phone);
                      setErr(null);
                    }}
                    className="flex-1 h-12 bg-[#f1f5f9] hover:bg-[#f1f5f9] text-[#0f172a] font-medium rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveNewPhone}
                    disabled={loading || newPhone.length < 13}
                    className="flex-1 h-12 bg-[#a78bfa] hover:bg-[#a78bfa]/90 disabled:bg-[#f1f5f9] disabled:text-gray-600 text-[#0f172a] font-semibold rounded-xl transition-all disabled:cursor-not-allowed"
                  >
                    {loading ? "Saving..." : "Save"}
                  </button>
                </div>
              </>
            ) : !otpSent ? (
              /* INITIAL STATE - SHOW PHONE & CONSENT */
              <>
                {/* Phone Display */}
                <div className="bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl p-4 mb-4">
                  <p className="text-xs text-gray-500 mb-1">Your number</p>
                  <p className="text-lg font-medium text-[#0f172a]">
                    {formatPhone(phone) || "Loading..."}
                  </p>
                </div>

                {/* Wrong number button */}
                <button
                  onClick={() => setEditingPhone(true)}
                  className="text-sm text-gray-500 hover:text-gray-600 transition-colors mb-5 flex items-center justify-center gap-1.5 w-full"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Wrong number? Edit it
                </button>

                {/* Consent */}
                <label className="flex items-start gap-3 cursor-pointer group mb-5">
                  <div className="relative mt-0.5">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-md border-2 transition-all duration-200 flex items-center justify-center ${
                      consent 
                        ? "bg-[#a78bfa] border-[#a78bfa]" 
                        : "border-[#e2e8f0] group-hover:border-[#e2e8f0] bg-[#f1f5f9]"
                    }`}>
                      {consent && (
                        <svg className="w-3 h-3 text-[#0f172a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-gray-500 leading-relaxed">
                    I agree to receive WhatsApp messages from Input, including coaching updates and reminders
                  </span>
                </label>

                {/* Send Code Button */}
                <button
                  onClick={sendOtp}
                  disabled={loading || !phone || !consent}
                  className="w-full h-12 bg-[#25D366] hover:bg-[#25D366]/90 disabled:bg-[#f1f5f9] disabled:text-gray-600 text-[#0f172a] font-semibold text-base rounded-xl transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Send WhatsApp Code
                    </>
                  )}
                </button>
              </>
            ) : (
              /* OTP ENTRY STATE */
              <>
                {/* OTP Input */}
                <div className="flex justify-center gap-3 mb-6">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <input
                      key={i}
                      ref={(el) => (inputRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={otp[i] ?? ""}
                      onChange={(e) => handleOtpInput(i, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(i, e)}
                      onFocus={(e) => e.target.select()}
                      className={`
                        w-11 h-13 bg-[#f1f5f9] border-2 rounded-xl
                        text-xl font-semibold text-[#0f172a] text-center
                        focus:outline-none transition-all duration-200
                        ${otp[i] 
                          ? "border-[#25D366] bg-[#25D366]/5" 
                          : "border-[#e2e8f0] focus:border-[#25D366]/50"
                        }
                      `}
                    />
                  ))}
                </div>

                {/* Verify Button */}
                <button
                  onClick={verifyOtp}
                  disabled={loading || otp.length !== 6}
                  className="w-full h-12 bg-[#a78bfa] hover:bg-[#a78bfa]/90 disabled:bg-[#f1f5f9] disabled:text-gray-600 text-[#0f172a] font-semibold text-base rounded-xl transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Verifying...
                    </>
                  ) : "Verify & Continue"}
                </button>

                {/* Resend / Wrong number */}
                <div className="flex justify-center gap-4 mt-5 text-sm">
                  <button
                    onClick={() => { setOtpSent(false); setOtp(""); }}
                    className="text-gray-500 hover:text-gray-600 transition-colors"
                  >
                    Resend code
                  </button>
                  <span className="text-[#475569]">•</span>
                  <button
                    onClick={() => { setOtpSent(false); setOtp(""); setEditingPhone(true); }}
                    className="text-gray-500 hover:text-gray-600 transition-colors"
                  >
                    Wrong number?
                  </button>
                </div>
              </>
            )}

            {/* Error */}
            {err && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-red-400 text-sm text-center">{err}</p>
              </div>
            )}
          </div>

          {/* Step Indicator */}
          <div className="mt-10">
            <StepIndicator current={4} total={5} />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center border-t border-[#e2e8f0]">
        <p className="text-gray-600 text-xs">
          © {new Date().getFullYear()} Input
        </p>
      </footer>

      {/* Custom styles for phone input */}
      <style>{`
        .react-tel-input .flag-dropdown {
          background: transparent !important;
        }
        .react-tel-input .selected-flag:hover,
        .react-tel-input .selected-flag:focus {
          background: #f1f5f9 !important;
        }
        .react-tel-input .country-list {
          background: #f1f5f9 !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 12px !important;
        }
        .react-tel-input .country-list .country:hover {
          background: #f1f5f9 !important;
        }
        .react-tel-input .country-list .country.highlight {
          background: rgba(167,139,250,0.2) !important;
        }
        .react-tel-input .country-list .country-name,
        .react-tel-input .country-list .dial-code {
          color: #0f172a !important;
        }
      `}</style>
    </div>
  );
}
